import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Download } from "lucide-react";
import {
  shippingMailApi,
  shippingMailPreviewApi,
  shopifyMailApi,
  type RenderedMail,
  type ShippingMailSettingsResponse,
  type ShippingMailTemplate,
  type ShippingMailVariant,
  type ShopifyMail,
  type ShopifyMailBlock,
  type ShopifyMailSample,
} from "../lib/settings-api";
import { useConfirm } from "@/components/confirm-dialog";
import { AdvancedSection, ErrorState, FormSection } from "@/components/layout";
import { PageHeader } from "@/components/page-header";
import { SaveBarProvider, useSaveBarForm } from "@/components/save-bar";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

/**
 * Alle Kundenmails an einem Ort: die Shopify-Versandmails (Liquid-Code zum
 * Einfügen in Shopify) und die eigenen, manuell verschickten SMTP-Mails.
 * Jede Vorlage mit Live-Vorschau aus Beispieldaten.
 */
export default function MailTemplates() {
  return (
    <SaveBarProvider>
      <PageHeader
        title="Mailvorlagen"
        meta="Texte der Kundenmails bearbeiten, mit Beispieldaten ansehen und – bei Shopify – als Code kopieren."
      />
      <ShopifyMailsSection />
      <SmtpMailsSection />
      <FormSection
        size="sm"
        title="Interne Mails"
        description="Freigabe-, Generierungs- und Versandmeldungen an dich sind fest im Code hinterlegt und hier nicht bearbeitbar."
      />
    </SaveBarProvider>
  );
}

// ── Gemeinsame Bausteine ────────────────────────────────────────────────────

async function copyText(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Fallback ohne Clipboard-API (z. B. unsicherer Kontext).
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
  }
  toast.success(`${label} kopiert`);
}

function downloadText(text: string, filename: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Rendert `draft` verzögert neu, sobald sich etwas ändert. */
function useDebouncedPreview<T, R>(
  draft: T | null,
  render: (draft: T) => Promise<R>,
): { result: R | null; error: string | null } {
  const [result, setResult] = useState<R | null>(null);
  const [error, setError] = useState<string | null>(null);
  const renderRef = useRef(render);
  renderRef.current = render;
  const key = JSON.stringify(draft);

  useEffect(() => {
    if (draft === null) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      renderRef
        .current(draft)
        .then((r) => {
          if (cancelled) return;
          setResult(r);
          setError(null);
        })
        .catch((e) => !cancelled && setError((e as Error).message));
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { result, error };
}

function MailPreview({ mail, error }: { mail: RenderedMail | null; error: string | null }) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="text-sm font-medium">Vorschau (Beispieldaten)</div>
      {error && <p className="text-sm text-destructive">Vorschau nicht aktuell: {error}</p>}
      {!mail ? (
        <Skeleton className="h-[560px] rounded-lg" />
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <div className="border-b px-3 py-2 text-sm">
            <span className="text-muted-foreground">Betreff: </span>
            <span className="font-medium break-words">{mail.subject}</span>
          </div>
          {/* sandbox ohne Rechte: kein Script, keine Navigation der Seite */}
          <iframe
            title="Mail-Vorschau"
            sandbox=""
            srcDoc={mail.html}
            className="h-[560px] w-full bg-white"
          />
        </div>
      )}
    </div>
  );
}

// ── Shopify-Versandmails ────────────────────────────────────────────────────

function ShopifyMailsSection() {
  const [mails, setMails] = useState<ShopifyMail[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    shopifyMailApi
      .list()
      .then((r) => setMails(r.mails))
      .catch((e) => setError((e as Error).message));
  }, []);

  return (
    <FormSection
      title="Shopify-Versandmails"
      description={
        <>
          Diese Mails verschickt Shopify. Hier Text anpassen, dann den Code kopieren und in Shopify
          unter <em>Einstellungen → Benachrichtigungen → Kundenbenachrichtigungen</em> bei der
          jeweiligen Mail über „Code bearbeiten“ komplett ersetzen. Betreff separat ins Feld
          „Betreff“. Logo und Akzentfarbe kommen aus Shopify.
        </>
      }
    >
      {error ? (
        <ErrorState message={error} />
      ) : !mails ? (
        <Skeleton className="h-96" />
      ) : (
        <Tabs defaultValue={mails[0].key}>
          <TabsList className="h-auto flex-wrap">
            {mails.map((m) => (
              <TabsTrigger key={m.key} value={m.key}>
                {m.name}
              </TabsTrigger>
            ))}
          </TabsList>
          {mails.map((m) => (
            // forceMount: ungespeicherte Entwürfe überleben den Tab-Wechsel.
            <TabsContent
              key={m.key}
              value={m.key}
              forceMount
              className="pt-3 data-[state=inactive]:hidden"
            >
              <ShopifyMailEditor
                mail={m}
                onSaved={(saved) =>
                  setMails((cur) => cur?.map((x) => (x.key === saved.key ? saved : x)) ?? null)
                }
              />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </FormSection>
  );
}

function ShopifyMailEditor({
  mail,
  onSaved,
}: {
  mail: ShopifyMail;
  onSaved: (mail: ShopifyMail) => void;
}) {
  const confirm = useConfirm();
  const [draft, setDraft] = useState<ShopifyMailBlock>(mail.block);
  const [sample, setSample] = useState<ShopifyMailSample>({ vorname: "Anna", kinder: ["Mia"] });
  const dirty = JSON.stringify(draft) !== JSON.stringify(mail.block);
  const label = `Shopify: ${mail.name}`;

  const { result, error } = useDebouncedPreview({ draft, sample }, (d) =>
    shopifyMailApi.preview(mail.key, d.draft, d.sample),
  );

  async function save(): Promise<boolean> {
    try {
      onSaved(await shopifyMailApi.save(mail.key, draft));
      toast.success(`${label}: gespeichert – Code jetzt in Shopify einfügen`);
      return true;
    } catch (e) {
      toast.error(`${label}: ${(e as Error).message}`);
      return false;
    }
  }

  useSaveBarForm({ label, dirty, save, discard: () => setDraft(mail.block) });

  async function resetToDefaults() {
    const ok = await confirm({
      title: "Auf Standardtext zurücksetzen?",
      description: "Gespeichert wird erst mit „Speichern“.",
      confirmLabel: "Zurücksetzen",
    });
    if (ok) setDraft(mail.defaults);
  }

  // Kopiert wird immer der aktuelle Entwurf (aus der Vorschau), damit
  // „ändern → kopieren“ auch ohne Speichern das Erwartete liefert.
  const liquid = result?.liquid ?? mail.liquid;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>
            In Shopify: <strong className="text-foreground">{mail.shopifyPath}</strong>
          </span>
          {dirty && <StatusBadge tone="warning">Nicht gespeichert</StatusBadge>}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => copyText(liquid, "Code")}>
            <Copy />
            Code kopieren
          </Button>
          <Button size="sm" variant="secondary" onClick={() => copyText(draft.subject, "Betreff")}>
            <Copy />
            Betreff kopieren
          </Button>
          <Button size="sm" variant="secondary" onClick={() => downloadText(liquid, mail.file)}>
            <Download />
            .liquid
          </Button>
          <Button size="sm" variant="ghost" onClick={resetToDefaults}>
            Auf Standard
          </Button>
        </div>

        <Field>
          <FieldLabel htmlFor={`sh-subject-${mail.key}`}>Betreff</FieldLabel>
          <Input
            id={`sh-subject-${mail.key}`}
            className="bg-card"
            value={draft.subject}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
          />
          <FieldDescription>
            Shopify-Liquid erlaubt, z. B. <code>{"{{ order_name }}"}</code> für die Bestellnummer.
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor={`sh-titel-${mail.key}`}>Überschrift</FieldLabel>
          <Input
            id={`sh-titel-${mail.key}`}
            className="bg-card"
            value={draft.titel}
            onChange={(e) => setDraft({ ...draft, titel: e.target.value })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={`sh-text-${mail.key}`}>Text (nach „Hallo [VORNAME],“)</FieldLabel>
          <Textarea
            id={`sh-text-${mail.key}`}
            className="bg-card"
            rows={5}
            value={draft.text}
            onChange={(e) => setDraft({ ...draft, text: e.target.value })}
          />
          <FieldDescription>
            Platzhalter: <code>[VORNAME]</code> <code>[KIND]</code> <code>[BESTELLNUMMER]</code>.
            [KIND] wird zu „Mia“ / „Mia und Leo“ oder „dein Kind“ – darum nach „für“ verwenden.
          </FieldDescription>
        </Field>
        <label className="flex items-center gap-3 text-sm">
          <Switch
            checked={draft.tracking}
            onCheckedChange={(v) => setDraft({ ...draft, tracking: v })}
          />
          Sendungsdaten anzeigen und Button zur Sendungsverfolgung
        </label>
        <Field>
          <FieldLabel htmlFor={`sh-button-${mail.key}`}>Button-Text</FieldLabel>
          <Input
            id={`sh-button-${mail.key}`}
            className="bg-card"
            value={draft.buttonText}
            onChange={(e) => setDraft({ ...draft, buttonText: e.target.value })}
          />
          <FieldDescription>
            Ohne Tracking (oder ohne Tracking-Link) heißt der Button immer „Bestellung ansehen“.
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor={`sh-schluss-${mail.key}`}>Schlusssatz (optional)</FieldLabel>
          <Textarea
            id={`sh-schluss-${mail.key}`}
            className="bg-card"
            rows={2}
            value={draft.schluss}
            onChange={(e) => setDraft({ ...draft, schluss: e.target.value })}
          />
        </Field>

        <AdvancedSection label="Beispieldaten der Vorschau">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor={`sh-sv-${mail.key}`}>Vorname Kunde</FieldLabel>
              <Input
                id={`sh-sv-${mail.key}`}
                className="bg-card"
                value={sample.vorname}
                onChange={(e) => setSample({ ...sample, vorname: e.target.value })}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`sh-sk-${mail.key}`}>Kinder (Komma-getrennt)</FieldLabel>
              <Input
                id={`sh-sk-${mail.key}`}
                className="bg-card"
                value={sample.kinder.join(", ")}
                onChange={(e) =>
                  setSample({
                    ...sample,
                    kinder: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .slice(0, 5),
                  })
                }
              />
            </Field>
          </div>
        </AdvancedSection>

        <AdvancedSection label="Liquid-Code ansehen">
          <Textarea
            readOnly
            className="bg-card font-mono text-xs"
            rows={14}
            value={liquid}
            onFocus={(e) => e.currentTarget.select()}
          />
        </AdvancedSection>
      </div>

      <MailPreview mail={result} error={error} />
    </div>
  );
}

// ── Eigene SMTP-Mails (manuell pro Bestellung) ──────────────────────────────

function SmtpMailsSection() {
  return (
    <FormSection
      title="Eigene Versandmails"
      description="Werden von uns verschickt, manuell pro Bestellung unter „Versand“ – erst als Test an dich, dann einmalig an den Kunden. Klartext; Absätze und Links werden automatisch formatiert."
    >
      <Tabs defaultValue="shipping">
        <TabsList>
          <TabsTrigger value="shipping">Versand</TabsTrigger>
          <TabsTrigger value="return">Rücksendung</TabsTrigger>
        </TabsList>
        <TabsContent value="shipping" forceMount className="pt-3 data-[state=inactive]:hidden">
          <SmtpMailEditor variant="shipping" label="Versandmail an Kunden" />
        </TabsContent>
        <TabsContent value="return" forceMount className="pt-3 data-[state=inactive]:hidden">
          <SmtpMailEditor variant="return" label="Versandmail – Rücksendung" />
        </TabsContent>
      </Tabs>
    </FormSection>
  );
}

function SmtpMailEditor({ variant, label }: { variant: ShippingMailVariant; label: string }) {
  const confirm = useConfirm();
  const [info, setInfo] = useState<ShippingMailSettingsResponse | null>(null);
  const [stored, setStored] = useState<ShippingMailTemplate | null>(null);
  const [draft, setDraft] = useState<ShippingMailTemplate | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    shippingMailApi
      .get(variant)
      .then((r) => {
        setInfo(r);
        setStored(r.template);
        setDraft(r.template);
      })
      .catch((e) => setLoadError((e as Error).message));
  }, [variant]);

  const { result, error } = useDebouncedPreview(draft, (d) =>
    shippingMailPreviewApi.preview(variant, d),
  );

  const dirty =
    stored !== null && draft !== null && JSON.stringify(draft) !== JSON.stringify(stored);

  async function save(): Promise<boolean> {
    if (!draft) return false;
    try {
      const r = await shippingMailApi.save(variant, draft);
      setInfo(r);
      setStored(r.template);
      setDraft(r.template);
      toast.success(`${label}: Vorlage gespeichert`);
      return true;
    } catch (e) {
      toast.error(`${label}: ${(e as Error).message}`);
      return false;
    }
  }

  useSaveBarForm({ label, dirty, save, discard: () => stored && setDraft(stored) });

  async function resetToDefaults() {
    if (!info) return;
    const ok = await confirm({
      title: "Auf Standardtext zurücksetzen?",
      description: "Gespeichert wird erst mit „Speichern“.",
      confirmLabel: "Zurücksetzen",
    });
    if (ok) setDraft(info.defaults);
  }

  if (loadError) return <ErrorState message={loadError} />;
  if (!info || !draft) return <Skeleton className="h-96" />;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => copyText(`${draft.subject}\n\n${draft.body}`, "Vorlage")}
          >
            <Copy />
            Text kopieren
          </Button>
          <Button size="sm" variant="ghost" onClick={resetToDefaults}>
            Auf Standard
          </Button>
          {dirty && <StatusBadge tone="warning">Nicht gespeichert</StatusBadge>}
        </div>
        <Field>
          <FieldLabel htmlFor={`ship-subject-${variant}`}>Betreff</FieldLabel>
          <Input
            id={`ship-subject-${variant}`}
            className="bg-card"
            value={draft.subject}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={`ship-body-${variant}`}>Text</FieldLabel>
          <Textarea
            id={`ship-body-${variant}`}
            className="bg-card font-mono"
            rows={16}
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          />
          <FieldDescription>
            Leerzeile = neuer Absatz. Links werden in der Mail automatisch klickbar.
          </FieldDescription>
        </Field>
        <AdvancedSection label="Verfügbare Platzhalter">
          <ul className="space-y-1 text-muted-foreground">
            {info.placeholders.map((p) => (
              <li key={p.key}>
                <code>{`{{${p.key}}}`}</code> – {p.description}
              </li>
            ))}
          </ul>
        </AdvancedSection>
      </div>
      <MailPreview mail={result} error={error} />
    </div>
  );
}
